/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { Menu } from "primereact/menu";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Image } from "primereact/image";
import {
  Boton,
  Titulo,
  CampoInformacion,
  Formulario,
  accessAPI,
  Sidebar,
  SubidorArchivos,
  formatFecha,
  urlBaseAPI,
  tituloBase,
  TablaFiltrable
} from "../../shared";

// Ficha de una acreditación: detalles editables (incluida la marca, que se
// sube como archivo) y el alcance, que son los vínculos con normas. Una norma
// puede estar acreditada con varias acreditaciones; cada vínculo registra su
// fecha de alta y de baja, y la suspensión (con historial) es por vínculo.
export default function AcreditacionIndividual() {
  const [acreditacion, setAcreditacion] = useState(null);
  // Cuál sidebar está abierto: "detalles", "norma" o "historial"
  const [sidebarAbierto, setSidebarAbierto] = useState(null);
  const [formLoader, setFormLoader] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [vinculoSeleccionado, setVinculoSeleccionado] = useState(null);

  const { acreditacionId } = useParams();
  const toast = useRef(null);
  const menuNormaRef = useRef(null);

  useEffect(() => {
    accessAPI(
      "GET",
      `admin/acreditacion/${acreditacionId}`,
      null,
      (respuesta) => {
        setAcreditacion(respuesta);
        document.title = respuesta.organismoAcreditacion;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar la acreditación",
          sticky: true
        });
      }
    );
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function editarDetalles(contenidoCampos) {
    setFormLoader(true);
    accessAPI(
      "PUT",
      `admin/acreditacion/${acreditacionId}`,
      contenidoCampos,
      (respuesta) => {
        setAcreditacion((prev) => ({ ...prev, ...respuesta }));
        document.title = respuesta.organismoAcreditacion;
        setSidebarAbierto(null);
        setFormLoader(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Acreditación actualizada correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar la acreditación",
          sticky: true
        });
      }
    );
  }

  // Suspende o reactiva la acreditación completa, con registro de fecha.
  // Las acreditaciones no se eliminan: se suspenden.
  function toggleSuspensionAcreditacion() {
    accessAPI(
      "PUT",
      `admin/acreditacion/${acreditacionId}/suspension`,
      null,
      (respuesta) => {
        setAcreditacion((prev) => ({ ...prev, ...respuesta }));
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: respuesta.suspendida
            ? "Acreditación suspendida"
            : "Acreditación reactivada",
          life: 3000
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar la suspensión",
          sticky: true
        });
      }
    );
  }

  // Da de alta una o varias normas en el alcance, con la fecha indicada (o
  // la de hoy). Las que ya estaban en el alcance se saltean.
  function agregarNormas(contenidoCampos) {
    setFormLoader(true);
    accessAPI(
      "POST",
      `admin/acreditacion/${acreditacionId}/normas`,
      {
        normaIds: contenidoCampos.normaIds || [],
        fechaAlta: contenidoCampos.fechaAlta || null
      },
      (respuesta) => {
        setAcreditacion((prev) => ({
          ...prev,
          norma_x_acreditacion: [
            ...(prev.norma_x_acreditacion || []),
            ...(respuesta.creados || [])
          ]
        }));
        setSidebarAbierto(null);
        setFormLoader(false);
        if (respuesta.yaVinculadas > 0) {
          toast.current?.show({
            severity: "warn",
            summary: "Altas registradas",
            detail: `${respuesta.creados.length} norma(s) dadas de alta; ${respuesta.yaVinculadas} ya estaban en el alcance`,
            life: 5000
          });
        } else {
          toast.current?.show({
            severity: "success",
            summary: "Agregadas",
            detail:
              respuesta.creados.length > 1
                ? `${respuesta.creados.length} normas dadas de alta en el alcance`
                : "Norma dada de alta en el alcance correctamente",
            life: 3000
          });
        }
      },
      (respuesta) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al agregar las normas",
          sticky: true
        });
      }
    );
  }

  // Suspende o reactiva un vínculo, con registro de fecha en su historial
  function toggleSuspension(vinculo) {
    accessAPI(
      "PUT",
      `admin/acreditacion/${acreditacionId}/normas/${vinculo.id}/suspension`,
      null,
      (respuesta) => {
        setAcreditacion((prev) => ({
          ...prev,
          norma_x_acreditacion: (prev.norma_x_acreditacion || []).map((v) =>
            v.id === vinculo.id ? respuesta : v
          )
        }));
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: respuesta.suspendida
            ? "Acreditación de la norma suspendida"
            : "Acreditación de la norma reactivada",
          life: 3000
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar la suspensión",
          sticky: true
        });
      }
    );
  }

  const menuVinculoItems = vinculoSeleccionado
    ? [
        {
          label: vinculoSeleccionado.suspendida
            ? "Reactivar acreditación"
            : "Suspender acreditación",
          icon: vinculoSeleccionado.suspendida ? "pi pi-play" : "pi pi-pause",
          command: () => {
            confirmDialog({
              message: vinculoSeleccionado.suspendida
                ? "¿Reactivar la acreditación de esta norma? Quedará registrada la fecha de nueva alta."
                : "¿Suspender la acreditación de esta norma? Quedará registrada la fecha de suspensión.",
              header: "Confirmar",
              icon: "pi pi-question-circle",
              acceptLabel: "Sí",
              rejectLabel: "No",
              accept: () => toggleSuspension(vinculoSeleccionado)
            });
          }
        },
        {
          label: "Ver historial",
          icon: "pi pi-history",
          command: () => setSidebarAbierto("historial")
        }
      ]
    : [];

  const detallesFormConfig = acreditacion
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "organismoAcreditacion",
                tipoitemformularioid: 1,
                etiqueta: "Organismo de acreditación",
                requerido: true,
                respuesta: acreditacion.organismoAcreditacion
              },
              {
                nombre: "pais",
                tipoitemformularioid: 1,
                etiqueta: "País del organismo",
                respuesta: acreditacion.pais
              },
              {
                nombre: "fechaEmision",
                tipoitemformularioid: 7,
                etiqueta: "Fecha de emisión",
                respuesta: acreditacion.fechaEmision
              },
              {
                nombre: "fechaValidez",
                tipoitemformularioid: 7,
                etiqueta: "Fecha de validez",
                respuesta: acreditacion.fechaValidez
              },
              {
                nombre: "linkAlcance",
                tipoitemformularioid: 1,
                etiqueta: "Link al alcance",
                respuesta: acreditacion.linkAlcance
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar",
            callback: editarDetalles,
            validar: true
          }
        ]
      }
    : null;

  const agregarNormaFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "normaIds",
            tipoitemformularioid: 12, // multiselect
            etiqueta: "Normas",
            opciones: "admin/norma",
            sinOpcionInicial: true,
            requerido: true
          },
          {
            nombre: "fechaAlta",
            tipoitemformularioid: 7, // fecha
            etiqueta: "Fecha (si queda en blanco es hoy)"
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Dar de alta",
        callback: agregarNormas,
        validar: true
      }
    ]
  };

  // Historial de suspensiones del vínculo seleccionado, del más reciente al
  // más viejo
  const historialSeleccionado = Array.isArray(
    vinculoSeleccionado?.historialSuspension
  )
    ? [...vinculoSeleccionado.historialSuspension].reverse()
    : [];

  const ETIQUETA_EVENTO = {
    ALTA: { texto: "Reactivación", severity: "success" },
    SUSPENSION: { texto: "Suspensión", severity: "danger" }
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={menuVinculoItems} popup ref={menuNormaRef} />

      {!acreditacion && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          <Skeleton width="20rem" height="2.5rem" />
          <Card>
            <Skeleton width="100%" height="8rem" />
          </Card>
          <Card>
            <Skeleton width="100%" height="5rem" />
          </Card>
        </div>
      )}

      {acreditacion && (
        <>
          <Titulo
            urlVolver="/acreditacion"
            subtitulo={acreditacion.pais || undefined}
            etiqueta={acreditacion.suspendida ? "Suspendida" : undefined}
            error={acreditacion.suspendida}
            botones={
              <>
                <SubidorArchivos
                  presignEndpoint="admin/archivo/presign"
                  confirmarEndpoint={`admin/acreditacion/${acreditacionId}/marca/confirmar`}
                  ubicacion="acreditaciones"
                  accept="image/*"
                  chooseLabel={
                    acreditacion.marcaArchivo
                      ? "Reemplazar marca"
                      : "Subir marca"
                  }
                  transparente
                  onSubido={(creados) => {
                    setAcreditacion((prev) => ({
                      ...prev,
                      marcaArchivo: creados[0]
                    }));
                  }}
                />
                <Boton
                  rojo={!acreditacion.suspendida}
                  transparente
                  callback={() => {
                    confirmDialog({
                      message: acreditacion.suspendida
                        ? "¿Reactivar esta acreditación? Quedará registrada la fecha de nueva alta."
                        : "¿Suspender esta acreditación? Quedará registrada la fecha de suspensión.",
                      header: "Confirmar",
                      icon: "pi pi-question-circle",
                      acceptLabel: "Sí",
                      rejectLabel: "No",
                      accept: toggleSuspensionAcreditacion
                    });
                  }}
                >
                  {acreditacion.suspendida ? "Reactivar" : "Suspender"}
                </Boton>
              </>
            }
          >
            {acreditacion.organismoAcreditacion}
          </Titulo>

          {/* Detalles de la acreditación, con la marca al lado */}
          <div className="grid">
            <div className="col-12 lg:col-8">
              <Card css={{ height: "100%" }}>
                <Titulo
                  botones={
                    <Boton
                      icono
                      negro
                      callback={() => {
                        setFormKey((k) => k + 1);
                        setSidebarAbierto("detalles");
                      }}
                    >
                      <i className="pi pi-pencil" />
                    </Boton>
                  }
                >
                  Detalles
                </Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Organismo de acreditación">
                    {acreditacion.organismoAcreditacion}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="País del organismo">
                    {acreditacion.pais || "—"}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Fecha de emisión">
                    {acreditacion.fechaEmision
                      ? formatFecha(acreditacion.fechaEmision)
                      : "—"}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Fecha de validez">
                    {acreditacion.fechaValidez
                      ? formatFecha(acreditacion.fechaValidez)
                      : "—"}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Link al alcance">
                    {acreditacion.linkAlcance ? (
                      <a
                        href={acreditacion.linkAlcance}
                        target="_blank"
                        rel="noreferrer"
                        css={{ color: "var(--principal)" }}
                      >
                        {acreditacion.linkAlcance}
                      </a>
                    ) : (
                      "—"
                    )}
                  </CampoInformacion>
                </div>
              </Card>
            </div>
            <div className="col-12 lg:col-4">
              <Card css={{ height: "100%" }}>
                <Titulo>Marca de acreditación</Titulo>
                {acreditacion.marcaArchivo ? (
                  <div css={{ textAlign: "center" }}>
                    <Image
                      src={`${urlBaseAPI()}/archivo/${acreditacion.marcaArchivo.hash}/preview`}
                      alt={acreditacion.marcaArchivo.nombredescarga}
                      preview
                      imageStyle={{
                        maxWidth: "100%",
                        maxHeight: "180px",
                        objectFit: "contain"
                      }}
                    />
                  </div>
                ) : (
                  <p css={{ color: "var(--grisTextos)", margin: 0 }}>
                    No hay marca cargada
                  </p>
                )}
              </Card>
            </div>
          </div>

          {/* Alcance: normas acreditadas */}
          <Card css={{ marginTop: "1em" }}>
            <Titulo
              botones={
                <Boton
                  icono
                  negro
                  callback={() => {
                    setFormKey((k) => k + 1);
                    setSidebarAbierto("norma");
                  }}
                >
                  <i className="pi pi-plus" />
                </Boton>
              }
            >
              Alcance
            </Titulo>
            <TablaFiltrable conFiltros={false}
              value={acreditacion.norma_x_acreditacion || []}
              emptyMessage="No hay normas en el alcance"
              size="small"
              nombreArchivo="alcance_acreditacion"
            >
              <Column
                header="Número"
                body={(vinculo) => vinculo.norma?.numero}
              />
              <Column
                header="Nombre"
                body={(vinculo) => (
                  <span
                    css={{ display: "flex", alignItems: "center", gap: "0.5em" }}
                  >
                    {vinculo.norma?.nombre}
                    {vinculo.suspendida && (
                      <Tag
                        value="Suspendida"
                        severity="danger"
                        css={{ fontSize: "0.7em" }}
                      />
                    )}
                  </span>
                )}
              />
              <Column
                header="Fecha de alta"
                body={(vinculo) =>
                  vinculo.fechaAlta ? formatFecha(vinculo.fechaAlta) : "—"
                }
                style={{ width: "10em" }}
              />
              <Column
                body={(vinculo) => (
                  <Boton
                    icono
                    chico
                    negro
                    callback={(e) => {
                      setVinculoSeleccionado(vinculo);
                      menuNormaRef.current.toggle(e);
                    }}
                  >
                    <i className="pi pi-ellipsis-v"></i>
                  </Boton>
                )}
                style={{ width: "3em", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>

          {/* Sidebar editar detalles */}
          <Sidebar
            visible={sidebarAbierto === "detalles"}
            position="right"
            onHide={() => setSidebarAbierto(null)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar acreditación</Titulo>
            {detallesFormConfig && (
              <Formulario
                key={`detalles-${formKey}`}
                config={detallesFormConfig}
                loader={formLoader}
              />
            )}
          </Sidebar>

          {/* Sidebar agregar norma al alcance */}
          <Sidebar
            visible={sidebarAbierto === "norma"}
            position="right"
            onHide={() => setSidebarAbierto(null)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Agregar norma al alcance</Titulo>
            <Formulario
              key={`norma-${formKey}`}
              config={agregarNormaFormConfig}
              loader={formLoader}
            />
          </Sidebar>

          {/* Sidebar historial de suspensiones del vínculo */}
          <Sidebar
            visible={sidebarAbierto === "historial"}
            position="right"
            onHide={() => setSidebarAbierto(null)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            {vinculoSeleccionado && (
              <div css={{ marginBottom: "50px" }}>
                <Titulo subtitulo={vinculoSeleccionado.norma?.nombre}>
                  Historial - {vinculoSeleccionado.norma?.numero}
                </Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Fecha de alta">
                    {vinculoSeleccionado.fechaAlta
                      ? formatFecha(vinculoSeleccionado.fechaAlta)
                      : "—"}
                  </CampoInformacion>
                </div>
                {historialSeleccionado.length > 0 ? (
                  historialSeleccionado.map((entrada, index) => (
                    <div
                      key={index}
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75em",
                        padding: "0.75em 0",
                        borderBottom: "1px solid var(--surface-200)"
                      }}
                    >
                      <Tag
                        value={
                          ETIQUETA_EVENTO[entrada.evento]?.texto ||
                          entrada.evento
                        }
                        severity={
                          ETIQUETA_EVENTO[entrada.evento]?.severity || "info"
                        }
                        css={{ fontSize: "0.75em", width: "7em" }}
                      />
                      <span>{formatFecha(entrada.fecha)}</span>
                    </div>
                  ))
                ) : (
                  <p css={{ color: "var(--grisTextos)" }}>
                    No hay suspensiones registradas para esta norma
                  </p>
                )}
              </div>
            )}
          </Sidebar>
        </>
      )}
    </>
  );
}
